import { createContext, useContext, useEffect, useState, useRef, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";

type AppRole = "admin" | "supplier";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  role: AppRole | null;
  profileId: string | null;
  orgId: string | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  session: null,
  role: null,
  profileId: null,
  orgId: null,
  loading: true,
  signOut: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [role, setRole] = useState<AppRole | null>(null);
  const [profileId, setProfileId] = useState<string | null>(null);
  const [orgId, setOrgId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const initializedRef = useRef(false);

  const fetchUserData = async (userId: string) => {
    try {
      const [roleRes, profileRes] = await Promise.all([
        supabase
          .from("user_roles")
          .select("role, org_id")
          .eq("user_id", userId),
        supabase
          .from("profiles")
          .select("id, org_id")
          .eq("user_id", userId),
      ]);

      const roles = roleRes.data || [];
      // Prefer admin org when the user has multiple roles (multi-org)
      const preferred =
        roles.find((r) => r.role === "admin") ||
        roles[0] ||
        null;

      if (preferred) {
        setRole(preferred.role as AppRole);
        setOrgId(preferred.org_id);
      } else {
        setRole(null);
        setOrgId(null);
      }

      const profiles = profileRes.data || [];
      const matchingProfile =
        (preferred && profiles.find((p) => p.org_id === preferred.org_id)) ||
        profiles[0] ||
        null;
      setProfileId(matchingProfile?.id ?? null);
    } catch (err) {
      console.error("fetchUserData error:", err);
      setRole(null);
      setOrgId(null);
      setProfileId(null);
    }
  };

  useEffect(() => {
    let cancelled = false;

    // Safety timeout: never let loading hang more than 8s
    const safety = setTimeout(() => {
      if (!cancelled) setLoading(false);
    }, 8000);

    // Get initial session and validate it against the auth server.
    // If the refresh token was revoked server-side, clear local state
    // instead of letting stale tokens leak into edge-function calls.
    supabase.auth
      .getSession()
      .then(async ({ data: { session: initSession } }) => {
        if (cancelled) return;
        if (!initSession) {
          setSession(null);
          setUser(null);
          return;
        }
        const { data: userData, error: userErr } = await supabase.auth.getUser();
        if (cancelled) return;
        if (userErr || !userData?.user) {
          // Stale/revoked session — purge it.
          await supabase.auth.signOut().catch(() => {});
          setSession(null);
          setUser(null);
          setRole(null);
          setProfileId(null);
          setOrgId(null);
          return;
        }
        setSession(initSession);
        setUser(userData.user);
        await fetchUserData(userData.user.id);
      })
      .catch((err) => {
        console.error("getSession error:", err);
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
        initializedRef.current = true;
      });

    // Then set up auth listener for subsequent changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        // Skip if this is the initial session (already handled above)
        if (!initializedRef.current) return;

        setSession(newSession);
        setUser(newSession?.user ?? null);

        if (newSession?.user) {
          // Fire and forget to avoid blocking the auth callback
          fetchUserData(newSession.user.id).finally(() => setLoading(false));
        } else {
          setRole(null);
          setProfileId(null);
          setOrgId(null);
          setLoading(false);
        }
      }
    );

    return () => {
      cancelled = true;
      clearTimeout(safety);
      subscription.unsubscribe();
    };
  }, []);

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setRole(null);
    setProfileId(null);
    setOrgId(null);
  };

  return (
    <AuthContext.Provider value={{ user, session, role, profileId, orgId, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};
